import React, { useState, useEffect } from 'react';
import { Box, Modal, Typography, IconButton, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import './PlatfromChargesEdit.css';
import { axiosInstance } from '../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';
import PlatformChargesDonutChart from './PlatformChargesDonutChart';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1000px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '16px',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
};

// Conversion function for koins to riyals
const convertKoinsToRiyals = (koins, conversionRate) => {
  // Use the provided conversion rate
  return parseFloat(koins) * conversionRate;
};

const PlatformChargesEdit = ({
  open,
  onClose,
  sessionData,
  onSaveSuccess,
  conversionRate = 7.5,
}) => {
  const [editData, setEditData] = useState({
    learnerCharges: { koins: 0, percentage: 100, riyal: 0 },
    klassCommission: { koins: 0, percentage: 0, riyal: 0 },
    serviceFee: { koins: 0, percentage: 0, riyal: 0 },
    instructorCommission: { koins: 0, percentage: 0, riyal: 0 },
  });
  const [isLoading, setIsLoading] = useState(false);
  const [validationMessage, setValidationMessage] = useState('');
  const [isValid, setIsValid] = useState(false);

  // Tooltip content
  const tooltipContent = {
    learnerCharges:
      'This is the total amount paid by the student, combining instructor payment, Klass commission, and platform service fees.',
    klassCommission: 'The commission earned by Klass from each session',
    serviceFee:
      'The portion of the total fee that goes to various services like payment processing, Cal.com',
    instructorCommission: 'The amount earned by the instructor for conducting this session',
    breakdown: 'Breakdown of the total fee',
  };

  useEffect(() => {
    if (sessionData && open) {
      const totalKoins = sessionData.total?.koins || sessionData.total_fee_koins || 0;
      const klassKoins = sessionData.klassCom?.koins || sessionData.klass_commission_koins || 0;
      const klassPercentage =
        sessionData.klassCom?.percentage || sessionData.klass_commission_percentage || 0;
      const serviceKoins = sessionData.cutout?.koins || sessionData.service_fee_koins || 0;
      const servicePercentage =
        sessionData.cutout?.percentage || sessionData.service_fee_percentage || 0;
      const instructorKoins =
        sessionData.teacher?.koins || sessionData.instructor_commission_koins || 0;
      const instructorPercentage =
        sessionData.teacher?.percentage || sessionData.instructor_commission_percentage || 0;

      setEditData({
        learnerCharges: {
          koins: totalKoins,
          percentage: 100,
          riyal: convertKoinsToRiyals(totalKoins, conversionRate),
        },
        klassCommission: {
          koins: klassKoins,
          percentage: klassPercentage,
          riyal: convertKoinsToRiyals(klassKoins, conversionRate),
        },
        serviceFee: {
          koins: serviceKoins,
          percentage: servicePercentage,
          riyal: convertKoinsToRiyals(serviceKoins, conversionRate),
        },
        instructorCommission: {
          koins: instructorKoins,
          percentage: instructorPercentage,
          riyal: convertKoinsToRiyals(instructorKoins, conversionRate),
        },
      });
    }
  }, [sessionData, open, conversionRate]);

  // Calculate derived values when total koins or percentages change
  const calculateValues = (totalKoins, klassPercentage, servicePercentage) => {
    const klassKoins = (parseFloat(klassPercentage) / 100) * parseFloat(totalKoins);
    const serviceKoins = (parseFloat(servicePercentage) / 100) * parseFloat(totalKoins);
    const instructorKoins = parseFloat(totalKoins) - klassKoins - serviceKoins;
    const instructorPercentage =
      parseFloat(totalKoins) > 0
        ? ((instructorKoins / parseFloat(totalKoins)) * 100).toFixed(2)
        : 0;

    return {
      learnerCharges: {
        koins: parseFloat(totalKoins),
        percentage: 100,
        riyal: convertKoinsToRiyals(totalKoins, conversionRate),
      },
      klassCommission: {
        koins: parseFloat(klassKoins.toFixed(2)),
        percentage: parseFloat(klassPercentage),
        riyal: convertKoinsToRiyals(klassKoins, conversionRate),
      },
      serviceFee: {
        koins: parseFloat(serviceKoins.toFixed(2)),
        percentage: parseFloat(servicePercentage),
        riyal: convertKoinsToRiyals(serviceKoins, conversionRate),
      },
      instructorCommission: {
        koins: parseFloat(instructorKoins.toFixed(2)),
        percentage: parseFloat(instructorPercentage),
        riyal: convertKoinsToRiyals(instructorKoins, conversionRate),
      },
    };
  };

  // Validation
  const validateData = () => {
    const total = parseFloat(editData.learnerCharges.koins);
    const breakdown =
      parseFloat(editData.klassCommission.koins) +
      parseFloat(editData.serviceFee.koins) +
      parseFloat(editData.instructorCommission.koins);
    const totalPercentage =
      parseFloat(editData.klassCommission.percentage) + parseFloat(editData.serviceFee.percentage);

    if (totalPercentage > 100) {
      setValidationMessage('Value should not exceed 100 Koins');
      setIsValid(false);
      return false;
    }

    // Allow for small floating point differences (0.01)
    const diff = Math.abs(breakdown - total);
    if (diff <= 0.01 && totalPercentage <= 100) {
      setValidationMessage('Perfect! 100% of the Koins have been allocated.');
      setIsValid(true);
      return true;
    }

    if (diff > 0.01) {
      setValidationMessage(`Total breakdown must match Learner Charges (${total} Koins)`);
      setIsValid(false);
      return false;
    }

    setValidationMessage('');
    setIsValid(false);
    return false;
  };

  useEffect(() => {
    if (open) {
      validateData();
    }
  }, [editData, open]);

  const handleInputChange = (field, value) => {
    if (field === 'learnerCharges') {
      let newTotalKoins = parseFloat(value) || 0;
      // Prevent negative numbers - automatically convert to 0
      if (newTotalKoins < 0) {
        newTotalKoins = 0;
      }
      setEditData({
        ...editData,
        learnerCharges: {
          koins: newTotalKoins,
          percentage: 100,
          riyal: convertKoinsToRiyals(newTotalKoins, conversionRate),
        },
      });
    } else if (field === 'klassCommission') {
      let newKoins = parseFloat(value) || 0;
      // Prevent negative numbers - automatically convert to 0
      if (newKoins < 0) {
        newKoins = 0;
      }
      setEditData({
        ...editData,
        klassCommission: {
          koins: newKoins,
          percentage:
            editData.learnerCharges.koins > 0
              ? ((newKoins / editData.learnerCharges.koins) * 100).toFixed(2)
              : 0,
          riyal: convertKoinsToRiyals(newKoins, conversionRate),
        },
      });
    } else if (field === 'serviceFee') {
      let newKoins = parseFloat(value) || 0;
      // Prevent negative numbers - automatically convert to 0
      if (newKoins < 0) {
        newKoins = 0;
      }
      setEditData({
        ...editData,
        serviceFee: {
          koins: newKoins,
          percentage:
            editData.learnerCharges.koins > 0
              ? ((newKoins / editData.learnerCharges.koins) * 100).toFixed(2)
              : 0,
          riyal: convertKoinsToRiyals(newKoins, conversionRate),
        },
      });
    } else if (field === 'instructorCommission') {
      let newKoins = parseFloat(value) || 0;
      // Prevent negative numbers - automatically convert to 0
      if (newKoins < 0) {
        newKoins = 0;
      }
      setEditData({
        ...editData,
        instructorCommission: {
          koins: newKoins,
          percentage:
            editData.learnerCharges.koins > 0
              ? ((newKoins / editData.learnerCharges.koins) * 100).toFixed(2)
              : 0,
          riyal: convertKoinsToRiyals(newKoins, conversionRate),
        },
      });
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.error('Please fix validation errors before saving');
      return;
    }

    try {
      setIsLoading(true);

      const updateData = {
        total_fee_koins: editData.learnerCharges.koins,
        // service_fee_percentage: parseFloat(editData.serviceFee.percentage),
        // klass_commission_percentage: parseFloat(editData.klassCommission.percentage),
        service_fee_koins: editData.serviceFee.koins,
        klass_commission_koins: editData.klassCommission.koins,
        instructor_commission_koins: editData.instructorCommission.koins,
      };

      const response = await axiosInstance.patch(
        `/admin/academic-fee-settings/${sessionData.id}`,
        updateData
      );

      if (response.status === 200) {
        toast.success('Platform charges updated successfully');
        onSaveSuccess?.();
        onClose();
      }
    } catch (error) {
      console.error('Error updating platform charges:', error);

      // Handle API error responses
      if (error.response && error.response.data) {
        const errorData = error.response.data;

        // Show the detail message from the server if available
        if (errorData.detail) {
          // Handle both string and array error formats
          if (Array.isArray(errorData.detail)) {
            // If detail is an array, show the first error message
            const firstError = errorData.detail[0];
            if (firstError && firstError.msg) {
              toast.error(firstError.msg);
            } else {
              toast.error('Validation error occurred. Please check your input.');
            }
          } else if (typeof errorData.detail === 'string') {
            // If detail is a string, show it directly
            toast.error(errorData.detail);
          } else {
            toast.error('Failed to update platform charges. Please try again.');
          }
        } else if (errorData.message) {
          toast.error(errorData.message);
        } else if (typeof errorData === 'string') {
          toast.error(errorData);
        } else {
          toast.error('Failed to update platform charges. Please try again.');
        }
      } else if (error.message) {
        toast.error(error.message);
      } else {
        toast.error('Failed to update platform charges. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setValidationMessage('');
    setIsValid(false);
    onClose();
  };

  // Prepare data for the donut chart
  const chartData = [
    {
      label: 'Klass Commission',
      value: editData.klassCommission.koins,
    },
    {
      label: 'Service/Platform Fee',
      value: editData.serviceFee.koins,
    },
    {
      label: 'Instructor Commission',
      value: editData.instructorCommission.koins,
    },
  ];

  if (!open) return null;

  return (
    <Modal open={open} onClose={handleClose} aria-labelledby="platform-charges-edit-modal">
      <Box sx={style}>
        {/* Header */}
        <Box className="platform-modal-header">
          <Typography className="platform-modal-title">
            {sessionData?.type || 'One-One-Sessions'}
          </Typography>
          <IconButton onClick={handleClose} className="platform-close-button">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box className="platform-modal-body">
          {/* Left side - Table */}
          <Box className="platform-table-section">
            {/* Table */}
            <Box className="platform-table">
              {/* Header Row */}
              <Box className="platform-table-header">
                <Box className="platform-header-cell">Label</Box>
                <Box className="platform-header-cell">Koins</Box>
                <Box className="platform-header-cell">%</Box>
                <Box className="platform-header-cell">Riyal</Box>
              </Box>

              {/* Learner Charges Row */}
              <Box className="platform-table-row learner-row">
                <Box className="platform-cell platform-label-cell">
                  <span>Learner Charges</span>
                  <Tooltip title={tooltipContent.learnerCharges}>
                    <InfoIcon className="platform-info-icon" />
                  </Tooltip>
                </Box>
                <Box className="platform-cell">
                  <input
                    type="number"
                    value={editData.learnerCharges.koins}
                    onChange={e => handleInputChange('learnerCharges', e.target.value)}
                    className="platform-input"
                    min="0"
                    step="0.01"
                    onKeyDown={e => {
                      // Prevent negative sign
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Box>
                <Box className="platform-cell">{editData.learnerCharges.percentage}</Box>
                <Box className="platform-cell">{editData.learnerCharges.riyal.toFixed(2)}</Box>
              </Box>

              {/* Breakdown Header */}
              <Box className="platform-breakdown-header">
                <span>Breakdown</span>
                <Tooltip title={tooltipContent.breakdown}>
                  <InfoIcon className="platform-info-icon" />
                </Tooltip>
              </Box>

              {/* Klass Commission Row */}
              <Box className="platform-table-row">
                <Box className="platform-cell platform-label-cell">
                  <span>Klass Commission</span>
                  <Tooltip title={tooltipContent.klassCommission}>
                    <InfoIcon className="platform-info-icon" />
                  </Tooltip>
                </Box>
                <Box className="platform-cell">
                  <input
                    type="number"
                    value={editData.klassCommission.koins}
                    onChange={e => handleInputChange('klassCommission', e.target.value)}
                    className="platform-input"
                    min="0"
                    step="0.01"
                    onKeyDown={e => {
                      // Prevent negative sign
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Box>
                <Box className="platform-cell">{editData.klassCommission.percentage}</Box>
                <Box className="platform-cell">{editData.klassCommission.riyal.toFixed(2)}</Box>
              </Box>

              {/* Service Fee Row */}
              <Box className="platform-table-row">
                <Box className="platform-cell platform-label-cell">
                  <span>Service/Platform Fee</span>
                  <Tooltip title={tooltipContent.serviceFee}>
                    <InfoIcon className="platform-info-icon" />
                  </Tooltip>
                  {editData.serviceFee.koins > editData.learnerCharges.koins && (
                    <span className="platform-error-icon">!</span>
                  )}
                </Box>
                <Box className="platform-cell">
                  <input
                    type="number"
                    value={editData.serviceFee.koins}
                    onChange={e => handleInputChange('serviceFee', e.target.value)}
                    className="platform-input"
                    min="0"
                    step="0.01"
                    onKeyDown={e => {
                      // Prevent negative sign
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Box>
                <Box className="platform-cell">{editData.serviceFee.percentage}</Box>
                <Box className="platform-cell">{editData.serviceFee.riyal.toFixed(2)}</Box>
              </Box>

              {/* Instructor Commission Row */}
              <Box className="platform-table-row">
                <Box className="platform-cell platform-label-cell">
                  <span>Instructor Commission</span>
                  <Tooltip title={tooltipContent.instructorCommission}>
                    <InfoIcon className="platform-info-icon" />
                  </Tooltip>
                  {editData.instructorCommission.percentage < 0 && (
                    <span className="platform-error-icon">D</span>
                  )}
                </Box>
                <Box className="platform-cell">
                  <input
                    type="number"
                    value={editData.instructorCommission.koins}
                    onChange={e => handleInputChange('instructorCommission', e.target.value)}
                    className="platform-input"
                    min="0"
                    step="0.01"
                    onKeyDown={e => {
                      // Prevent negative sign
                      if (e.key === '-') {
                        e.preventDefault();
                      }
                    }}
                  />
                </Box>
                <Box className="platform-cell">{editData.instructorCommission.percentage}</Box>
                <Box className="platform-cell">
                  {editData.instructorCommission.riyal.toFixed(2)}
                </Box>
              </Box>
            </Box>

            {/* Validation Message */}
            {validationMessage && (
              <Box className={`platform-validation ${isValid ? 'success' : 'error'}`}>
                {validationMessage}
              </Box>
            )}
          </Box>

          {/* Right side - Donut Chart */}
          <Box className="platform-chart-section">
            <PlatformChargesDonutChart
              data={chartData}
              centerValue={editData.learnerCharges.koins.toString()}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box className="platform-modal-footer">
          <button className="platform-cancel-button" onClick={handleClose} disabled={isLoading}>
            Cancel
          </button>
          <button
            className={`platform-save-button ${!isValid ? 'disabled' : ''}`}
            onClick={handleSave}
            disabled={isLoading || !isValid}
          >
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </Box>
      </Box>
    </Modal>
  );
};

export default PlatformChargesEdit;
